import { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { getContent } from "../lib/api";
import { Sparkles, Search, ChevronLeft, ChevronRight, X } from "lucide-react";

const PAGE = 18;

export default function Names() {
  const { t } = useApp();
  const [all, setAll] = useState([]);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { getContent("names").then(setAll); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter(n =>
      n.translit.toLowerCase().includes(term) ||
      n.fr.toLowerCase().includes(term) ||
      n.ar.includes(term)
    );
  }, [all, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const slice = filtered.slice(page * PAGE, page * PAGE + PAGE);

  return (
    <div className="space-y-6 fade-in" data-testid="names-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><Sparkles className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.names}</h1>
          <p className="text-sm text-nour-text2">99 Asmaul Husna</p>
        </div>
      </header>

      <div className="nour-card p-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-nour-text2" />
        <input
          data-testid="names-search"
          className="flex-1 bg-transparent outline-none text-sm"
          placeholder={t.search}
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="names-grid">
        {slice.map(n => (
          <button
            key={n.id}
            onClick={() => setSelected(n)}
            data-testid={`name-${n.id}`}
            className="nour-card p-4 text-left hover:bg-emerald-50 transition"
          >
            <div className="font-arabic-quran text-2xl text-nour-primary mb-1" dir="rtl">{n.ar}</div>
            <div className="font-heading font-semibold text-sm">{n.translit}</div>
            <div className="text-xs text-nour-text2">{n.fr}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <button className="btn-ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="names-prev"><ChevronLeft className="w-4 h-4"/> {t.previous}</button>
        <span className="text-sm text-nour-text2">{page + 1} / {totalPages}</span>
        <button className="btn-primary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="names-next">{t.next} <ChevronRight className="w-4 h-4"/></button>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)} data-testid="name-modal">
          <div className="nour-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end"><button onClick={() => setSelected(null)} data-testid="name-modal-close"><X className="w-5 h-5"/></button></div>
            <div className="text-center">
              <div className="font-arabic-quran text-5xl text-nour-primary mb-3" dir="rtl">{selected.ar}</div>
              <div className="font-heading text-2xl font-bold">{selected.translit}</div>
              <div className="text-lg text-nour-primary font-semibold mt-1">{selected.fr}</div>
              <p className="text-sm text-nour-text2 mt-4 leading-relaxed">{selected.meaning_fr}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
